import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Zap,
  TrendingUp,
  Shield,
  Clock,
  MapPin,
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Users,
  Headphones,
  DollarSign,
  Wrench,
  Globe,
  Award,
  Cpu,
  FileCheck,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  fadeInUp,
  Counter,
} from "@/components/animations/ScrollAnimations";
import { Link } from "react-router-dom";
import { SEO, cpoPageSchema } from "@/components/SEO";

import chargingSite1 from "@/assets/charging-site-1.jpg";
import chargingSite2 from "@/assets/charging-site-2.jpg";
import chargingSite3 from "@/assets/charging-site-3.jpg";
import chargingSite4 from "@/assets/charging-site-4.jpg";
import chargingSite5 from "@/assets/charging-site-5.jpg";
import chargingSite6 from "@/assets/charging-site-6.jpg";

const benefits = [
  {
    icon: DollarSign,
    title: "Revenue Maximization",
    description:
      "Dynamic pricing algorithms and demand forecasting to optimize your charging revenue across all sites.",
  },
  {
    icon: Shield,
    title: "NEVI, State & Utility Regulatory Compliance",
    description:
      "Stay compliant with federal NEVI requirements, state regulations, and industry standards automatically.",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    description: "Comprehensive dashboards showing utilization, revenue, uptime, and customer behavior insights.",
  },
  {
    icon: Wrench,
    title: "Managed Maintenance",
    description: "Proactive monitoring and predictive maintenance to keep your network running at 99.9% uptime.",
  },
  {
    icon: Users,
    title: "Driver Network Access",
    description: "Instant access to thousands of EV drivers through our mobile app and roaming partnerships.",
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    description: "Round-the-clock technical support for both operators and drivers ensuring seamless operations.",
  },
];

const partnershipTiers = [
  {
    name: "Starter",
    description: "Perfect for small businesses entering the EV charging market",
    features: [
      "Up to 10 charging stations",
      "Hardware and EPC selection",
      "Basic analytics dashboard",
      "Standard payment processing",
      "Email and Phone support",
      "Monthly Sync",
    ],
    highlighted: false,
  },
  {
    name: "Professional",
    description: "Ideal for growing networks with multiple locations",
    features: [
      "Up to 100 charging stations",
      "Advanced analytics & insights",
      "Special Pricing for Hardware and EPC",
      "Dynamic pricing engine",
      "Priority phone support",
      "Custom branding options",
      "API access",
      "Roaming network access",
      "Quarterly business reviews",
    ],
    highlighted: true,
  },
  {
    name: "Enterprise",
    description: "For large-scale deployments and utility partners",
    features: [
      "Unlimited charging stations",
      "Lowest Pricing for Hardware and EPC",
      "Enterprise analytics suite",
      "AI-powered optimization",
      "Dedicated account manager",
      "White-label solutions",
      "Full API integration",
      "Load management",
      "Custom SLA agreements",
      "Federal and State compliance support",
    ],
    highlighted: false,
  },
];

const process = [
  {
    step: "01",
    title: "Consultation",
    description: "We analyze your sites, assess electrical capacity, and develop a customized deployment plan.",
    icon: Users,
  },
  {
    step: "02",
    title: "Hardware Selection",
    description: "Choose from our portfolio of certified chargers optimized for your specific use case.",
    icon: Cpu,
  },
  {
    step: "03",
    title: "Installation & Commissioning",
    description: "Our certified installers handle everything from permitting to final commissioning.",
    icon: Wrench,
  },
  {
    step: "04",
    title: "Launch & Optimize",
    description: "Go live with full platform access and ongoing optimization support.",
    icon: TrendingUp,
  },
];

const chargingSites = [
  { image: chargingSite1, location: "Hotel & Hospitality" },
  { image: chargingSite2, location: "Commercial Parking" },
  { image: chargingSite3, location: "Highway Corridor" },
  { image: chargingSite4, location: "Retail Center" },
  { image: chargingSite5, location: "Multi-family Housing" },
  { image: chargingSite6, location: "Mixed-use Development" },
];

const CPO = () => {
  return (
    <main className="min-h-screen bg-background">
      <SEO
        title="Become a Charge Point Operator (CPO) - Fast Charging & Fleet Charging Partner"
        description="Partner with EVGateway to deploy and manage EV charging networks. Become a Charge Point Operator with DC Fast Charging, Fleet Charging, and Public Charging infrastructure. NEVI compliant, 99.9% uptime SLA, 24/7 support."
        keywords="charge point operator, CPO, EV charging partner, fast charging network, fleet charging, public charging, EV charging business, charging station operator, NEVI compliant, EV infrastructure partner"
        canonicalUrl="/cpo"
        structuredData={cpoPageSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 text-primary text-sm font-medium mb-6"
              >
                <Zap className="h-4 w-4" />
                Charge Point Operator Program
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Build Your <span className="text-gradient">EV Charging Empire</span>
              </h1>

              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
                Partner with EVGateway to deploy, manage, and monetize EV charging infrastructure. Our turnkey CPO
                platform handles everything so you can focus on growth.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="hero" size="lg" className="group" asChild>
                    <Link to="/contact">
                      Become a Partner
                      <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="outline" size="lg" className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50">
                    Download CPO Guide
                  </Button>
                </motion.div>
              </div>
            </div>
          </ScrollReveal>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {[
              { value: 50, suffix: "+", label: "Sites Deployed" },
              { value: 98.5, suffix: "%", label: "Uptime SLA" },
              { value: 10, suffix: "K+", label: "Monthly Sessions" },
              { value: 24, suffix: "/7", label: "Support" },
            ].map((stat, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.03 }}
                className={`text-center p-6 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
              >
                <div className={`text-4xl md:text-5xl font-bold ${idx % 2 === 0 ? "text-primary" : "text-accent"}`}>
                  <Counter target={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-muted-foreground mt-2 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Why Partner with <span className="text-gradient">EVGateway?</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Everything you need to launch and scale a profitable EV charging network.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {benefits.map((benefit, idx) => (
              <StaggerItem key={idx}>
                <motion.div 
                  whileHover={{ y: -5, scale: 1.02 }} 
                  className={`bg-background/80 backdrop-blur-sm border border-border/30 rounded-3xl p-6 h-full transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <benefit.icon className={`h-6 w-6 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Partnership <span className="text-gradient">Tiers</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Flexible options designed to match your business scale and goals.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" staggerDelay={0.15}>
            {partnershipTiers.map((tier, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  className={`relative rounded-3xl p-8 h-full transition-all duration-300 ${
                    tier.highlighted
                      ? "bg-gradient-to-b from-primary to-primary/90 text-primary-foreground border-2 border-primary shadow-2xl shadow-primary/20"
                      : "bg-background/80 backdrop-blur-sm border border-border/30 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10"
                  }`}
                >
                  {tier.highlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-accent-foreground text-sm font-medium rounded-full">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                  <p
                    className={`text-sm mb-6 ${tier.highlighted ? "text-primary-foreground/80" : "text-muted-foreground"}`}
                  >
                    {tier.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-3">
                        <CheckCircle2
                          className={`h-5 w-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? "text-primary-foreground" : "text-primary"}`}
                        />
                        <span
                          className={`text-sm ${tier.highlighted ? "text-primary-foreground/90" : "text-muted-foreground"}`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={tier.highlighted ? "secondary" : "outline"} className="w-full rounded-xl" asChild>
                    <Link to="/contact">Get Started</Link>
                  </Button>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Your Path to <span className="text-gradient">Success</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                From consultation to launch, we guide you every step of the way.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto">
            <StaggerContainer className="space-y-6" staggerDelay={0.15}>
              {process.map((item, idx) => (
                <StaggerItem key={idx}>
                  <motion.div 
                    whileHover={{ x: 10 }} 
                    className={`flex gap-6 items-start p-6 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                        <item.icon className={`h-8 w-8 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-4 mb-2">
                        <span className={`text-4xl font-bold ${idx % 2 === 0 ? "text-primary/30" : "text-accent/30"}`}>{item.step}</span>
                        <h3 className="text-xl font-semibold">{item.title}</h3>
                      </div>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Our <span className="text-gradient">Deployed Sites</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Real charging infrastructure deployed across North America.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-4" staggerDelay={0.1}>
            {chargingSites.map((site, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="group relative rounded-3xl overflow-hidden aspect-[4/3] border border-border/30 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
                  tabIndex={0}
                  role="figure"
                  aria-label={`${site.location} charging station`}
                >
                  <img
                    src={site.image}
                    alt={`${site.location} EV charging infrastructure deployed by EVGateway`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 text-foreground">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{site.location}</span>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Join the EV Revolution?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Schedule a consultation with our CPO team to discuss your goals and get a customized deployment plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="hero" size="lg" className="group" asChild>
                    <a
                      href="https://outlook.office365.com/book/EvGatewayPortalDemos@evgateway.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </a>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50"
                  >
                    Call (949) 606-8396
                  </Button>
                </motion.div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default CPO;